///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import _sortBy from "lodash/sortBy";

import { getActiveBids } from "../api/bids";
import * as types from "../helpers/types";
import getLeastProgressedMode from "../helpers/getLeastProgressedMode";
import getBidTags from "../helpers/getBidTags";
import { default as filterBids } from "../helpers/filterActiveBids";
import bindActiveBidUsers from "../helpers/bindActiveBidUsers";

export const initialState = {
  isFetching: false,
  isFailure: false,
  pendingBids: [],
  inProgressBids: [],
  submittedBids: [],
  feedbackBids: [],
  totalCount: 0,
  sort: {
    field: null,
    order: null
  },
  filters: {
    sortByRfqCloseDate: true,
    bidsIOwn: false,
    bidsIAmInTheTeam: false,
    bidsIHaveAssignments: false,
    users: [],
    tags: [],
    bidStatus: [],
    selectedUsers: null,
    selectedBidStatus: null,
    selectedTags: null,
    searchTerm: "",
    bidTypes: [],
    selectedBidTypes: null
  }
};

const groupBids = (activeBids, user) => {
  const groupedBids = {
    [types.status.pending]: [],
    [types.status.inProgress]: [],
    [types.status.submitted]: [],
    [types.status.feedback]: []
  };

  for (let bid of activeBids) {
    const leastProgressedMode = getLeastProgressedMode(bid.modes, user);
    if (leastProgressedMode.status === types.status.rejected) {
      if (
        user.role !== types.userRole.tlPricingAnalyst &&
        user.role !== types.userRole.assetBasedPricingUser &&
        user.userRoleForManagementUser !== types.userRole.tlPricingAnalyst &&
        user.userRoleForManagementUser !== types.userRole.assetBasedPricingUser
      ) {
        groupedBids[types.status.pending].push(bid);
      }
    } else {
      groupedBids[leastProgressedMode.status].push(bid);
    }
  }

  return groupedBids;
};

const getActiveBidsDistinctUsers = activeBids => {
  const users = [];

  const pushIfNotPresent = user =>
    user &&
    !users.find(({ value }) => value === user.userId) &&
    users.push({
      label: user.fullName,
      value: user.userId,
      userId: user.userId
    });

  activeBids &&
    activeBids.forEach(({ modes, mainSalesRep }) => {
      pushIfNotPresent(mainSalesRep);
      modes.forEach(({ modeOwner }) => pushIfNotPresent(modeOwner));
    });

  return users;
};

const getActiveBidsDistinctTags = (activeBids, user) => {
  const tags = [];
  activeBids &&
    activeBids.forEach(bid =>
      getBidTags(bid, user).forEach(
        ({ tag }) =>
          !tags.find(({ value }) => tag === value) &&
          tags.push({ value: tag, label: tag })
      )
    );
  return tags;
};

const activeBidsSlice = createSlice({
  name: "activeBids",
  initialState,
  reducers: {
    requestActiveBids: state => {
      state.isFetching = true;
      state.isFailure = false;
      state.sort = initialState.sort;
      state.filters = initialState.filters;
    },
    receiveActiveBidsSuccess: (state, { payload }) => {
      for (let bid of payload.activeBids) {
        bid.matchesSearch = true;
      }
      const sortedBids = _sortBy(payload.activeBids, "deadlineDateUTC");
      const groupedBids = groupBids(sortedBids, payload.user);
      state.pendingBids = groupedBids[types.status.pending];
      state.inProgressBids = groupedBids[types.status.inProgress];
      state.submittedBids = groupedBids[types.status.submitted];
      state.feedbackBids = groupedBids[types.status.feedback];

      state.totalCount =
        state.pendingBids.length +
        state.inProgressBids.length +
        state.submittedBids.length +
        state.feedbackBids.length;

      state.isFetching = false;
      state.isFailure = false;
      if (payload.activeBids && payload.user && payload.user.role) {
        state.filters.users = getActiveBidsDistinctUsers(payload.activeBids);
        state.filters.bidStatus = types.bidStatusOptions.filter(
          ({ value }) => groupedBids[value] && groupedBids[value].length !== 0
        );
        state.filters.tags = getActiveBidsDistinctTags(
          payload.activeBids,
          payload.user
        );
        state.filters.bidTypes = types.bidTypes;
        filterBids(state, payload.user, window.location.pathname === "/");
      }
    },
    receiveActiveBidsFailure: state => {
      state.isFetching = false;
      state.isFailure = true;
    },
    filterActiveBids: (
      state,
      { payload: { field, value, user, cardView } }
    ) => {
      if (field) {
        state.filters[field] = value;
      }
      filterBids(state, user, cardView);
    },
    sortListView: ({ sort }, { payload: column }) => {
      if (sort.field === column && sort.order === types.order.ascending) {
        sort.order = types.order.descending;
      } else if (sort.field === column) {
        sort.field = sort.order = null;
      } else {
        sort.field = column;
        sort.order = types.order.ascending;
      }
    },
    resetFilters: (state, { payload }) => {
      state.filters = {
        ...initialState.filters,
        users: state.filters.users,
        bidStatus: state.filters.bidStatus,
        tags: state.filters.tags,
        bidTypes: state.filters.bidTypes
      };
      filterBids(state, payload.user, window.location.pathname === "/");
    }
  }
});

export const {
  requestActiveBids,
  receiveActiveBidsSuccess,
  receiveActiveBidsFailure,
  filterActiveBids,
  sortListView,
  resetFilters
} = activeBidsSlice.actions;

export const activeBidsReducer = activeBidsSlice.reducer;

export const fetchActiveBids = () => async (dispatch, getState) => {
  const { user } = getState().user;
  let response;

  dispatch(requestActiveBids());

  try {
    response = await getActiveBids(user.userRoleForManagementUser);
  } catch {
    dispatch(receiveActiveBidsFailure());
    return;
  }

  bindActiveBidUsers(response.data);
  dispatch(
    receiveActiveBidsSuccess({ activeBids: response.data.activeBids, user })
  );
};
